import { useCallback, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useMiniNotchRegion } from '../components/mini/notch/notch-region'
import { trackNotchAction } from '../track-notch'
import { detectedTimeZone } from './calendar'
import { SetupCallBooked } from './nested/booked'
import { SETUP_CALL_FACE_WIDTH, SetupCallField, faceVariants, type SetupCallFieldTone } from './nested/face'
import { SetupCallInvite } from './nested/invite'
import { SetupCallPicker } from './nested/picker'
import { SetupCallTimeZones } from './nested/time-zones'
import { useSetupCallState } from './setup-call-api'
import type { SetupCallOffer } from './use-setup-call-offer'

interface SetupCallPanelProps {
  offer: SetupCallOffer
}

const FIELD_TONE = { invite: 'invite', book: 'quiet', zone: 'quiet', booked: 'success' } as const satisfies Record<
  string,
  SetupCallFieldTone
>

// From the top of the notch, as the prototype lays them out: a title 40 down; the booked badge adds its own 12 under 30
const FIRST_INK_TOP = { invite: 40, book: 40, zone: 40, booked: 30 } as const

/** What hangs under the ears: the invite grows in place into the picker, the zones, then the confirmation. */
export function SetupCallPanel({ offer }: SetupCallPanelProps) {
  const { open, moment, show, settle, maybeLater, neverShow } = offer
  const booking = useSetupCallState(true).data?.booking ?? null
  const [zone, setZone] = useState(() => booking?.timezone ?? detectedTimeZone())
  const view = open?.view
  const region = useMiniNotchRegion()
  // The notch's row, physical bleed included, which the first ink sits under
  const earsHeight = region.height + (region.hasNotch ? 1 : 0)

  const onBooked = useCallback(() => {
    trackNotchAction('setup call', { step: booking ? 'rescheduled' : 'booked' })
    show('booked')
  }, [booking, show])

  const onInviteIgnored = useCallback(() => {
    trackNotchAction('setup call', { step: 'invite ignored' })
    settle()
  }, [settle])

  // Esc steps back one view at a time: zones to the picker, the picker to the ears, the invite to later
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !view) return
      event.preventDefault()
      trackNotchAction('setup call', { step: 'escaped', view })
      if (view === 'zone') show('book')
      else if (view === 'invite' && moment === 'first') maybeLater()
      else settle()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [maybeLater, moment, settle, show, view])

  if (!open) return null

  return (
    // Out past the slot's own inset, so the field reaches the cutout's edges
    <div data-setup-call-field="" className="relative -m-1" style={{ width: SETUP_CALL_FACE_WIDTH }}>
      <SetupCallField tone={FIELD_TONE[open.view]} earsHeight={earsHeight} />
      <motion.div
        key={view}
        className="relative flex origin-top flex-col px-3 pb-3"
        style={{ paddingTop: Math.max(0, FIRST_INK_TOP[open.view] - earsHeight) }}
        variants={faceVariants}
        initial="hidden"
        animate="visible"
      >
        {view === 'invite' && (
          <SetupCallInvite
            moment={moment ?? 'first'}
            isPeek={open.isPeek}
            onPick={() => {
              trackNotchAction('setup call', { step: 'pick a time' })
              show('book')
            }}
            onLater={maybeLater}
            onNever={neverShow}
            onSettle={onInviteIgnored}
          />
        )}
        {view === 'book' && (
          <SetupCallPicker
            zone={zone}
            booking={booking}
            onChangeZone={() => show('zone')}
            onBooked={onBooked}
            onCancel={() => {
              trackNotchAction('setup call', { step: 'pick canceled', isReschedule: !!booking })
              settle()
            }}
          />
        )}
        {view === 'zone' && (
          <SetupCallTimeZones
            zone={zone}
            onSave={(next) => {
              if (next !== zone) trackNotchAction('setup call', { step: 'time zone changed', zone: next })
              setZone(next)
              show('book')
            }}
            onCancel={() => show('book')}
          />
        )}
        {view === 'booked' && booking && (
          <SetupCallBooked
            booking={booking}
            zone={zone}
            onReschedule={() => {
              trackNotchAction('setup call', { step: 'reschedule opened' })
              show('book')
            }}
            onDone={settle}
          />
        )}
      </motion.div>
    </div>
  )
}
